import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AutomationJob } from '@/hooks/useAutomationJobs';
import { Loader2, AlertTriangle } from 'lucide-react';

interface EditJobModalProps {
  job: AutomationJob | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (jobId: string, updates: { total_images_target?: number; job_name?: string }) => Promise<void>;
}

export const EditJobModal = ({ job, open, onOpenChange, onSave }: EditJobModalProps) => {
  const [jobName, setJobName] = useState('');
  const [targetImages, setTargetImages] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (job) {
      setJobName(job.job_name);
      setTargetImages(job.total_images_target.toString());
      setError(null);
    }
  }, [job]);

  if (!job) return null;

  const currentProgress = job.success_count || job.images_generated;
  const newTarget = parseInt(targetImages) || 0;
  const isValidTarget = newTarget >= currentProgress;
  const willComplete = newTarget > 0 && newTarget === currentProgress;

  const handleSave = async () => {
    if (!isValidTarget) {
      setError(`Target must be at least ${currentProgress} (already generated)`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updates: { total_images_target?: number; job_name?: string } = {};
      
      if (jobName !== job.job_name) {
        updates.job_name = jobName;
      }
      
      if (newTarget !== job.total_images_target) {
        updates.total_images_target = newTarget;
      }

      if (Object.keys(updates).length === 0) {
        onOpenChange(false);
        return;
      }

      await onSave(job.id, updates);
      onOpenChange(false);
    } catch (err) {
      setError('Failed to update job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-background/95 backdrop-blur-md border-border/50">
        <DialogHeader>
          <DialogTitle>Edit Job</DialogTitle>
          <DialogDescription>
            Modify the job name or target image count.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="jobName">Job Name</Label>
            <Input
              id="jobName"
              value={jobName}
              onChange={(e) => setJobName(e.target.value)}
              placeholder="Enter job name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="targetImages">Target Images</Label>
            <Input
              id="targetImages"
              type="number"
              min={currentProgress}
              value={targetImages}
              onChange={(e) => {
                setTargetImages(e.target.value);
                setError(null);
              }}
              placeholder="Enter target"
            />
            <p className="text-xs text-muted-foreground">
              Current progress: {currentProgress} images generated
            </p>
          </div>

          {!isValidTarget && targetImages && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Target cannot be less than {currentProgress} (already generated images)
              </AlertDescription>
            </Alert>
          )}

          {willComplete && (
            <Alert>
              <AlertDescription>
                Setting target to {currentProgress} will mark this job as completed.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !isValidTarget}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
